import {
  mdiEmail,
  mdiPhone,
  mdiChat,
  mdiCreditCard,
  mdiShield,
} from '@mdi/js';

import type {
  FeatureComparisonColumn,
  FeatureComparisonRow,
  FAQItem,
  SupportOption,
  InfoCard,
} from './types';

// Route name owned by the pricing screen (single-screen → local consts, #878).
export const PRICING_ROUTE_NAME = {
  PRICING: 'pricing',
};

export const featureComparisonColumns: FeatureComparisonColumn[] = [
  { name: 'feature', label: 'Feature', field: 'feature', align: 'left' as const, style: 'width: 40%' },
  { name: 'basic', label: 'Basic', field: 'basic', align: 'center' as const },
  { name: 'standard', label: 'Standard', field: 'standard', align: 'center' as const },
  { name: 'premium', label: 'Premium', field: 'premium', align: 'center' as const },
];

export const featureComparisonRows: FeatureComparisonRow[] = [
  // Core Management
  { id: 'category-core', feature: 'Core Management', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'today-staff', feature: 'Today Staff (View all Staff, work-in/out status)', basic: true, standard: true, premium: true },
  { id: 'staff-management', feature: 'Staff Management (CRUD staff, update skill)', basic: true, standard: true, premium: true },
  { id: 'service-management', feature: 'Service Management (CRUD services)', basic: true, standard: true, premium: true },
  { id: 'whiteboard', feature: 'Whiteboard (Quick note)', basic: true, standard: true, premium: true },
  { id: 'promotions', feature: 'Promotions (View current promotions)', basic: true, standard: true, premium: true },

  // Customer Operations
  { id: 'category-customer', feature: 'Customer Operations', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'customer-registration', feature: 'Customer Registrations', basic: true, standard: true, premium: true },
  { id: 'customer-checkin', feature: 'Customer Check-in', basic: true, standard: true, premium: true },
  { id: 'customer-checkout', feature: 'Customer Check out (Pay, tip, print bills)', basic: true, standard: true, premium: true },
  { id: 'pos', feature: 'POS (Payment+Receipt)', basic: true, standard: true, premium: true },
  { id: 'advanced-pos', feature: 'Advanced POS (Notes, add-ons, split payments)', basic: false, standard: true, premium: true },

  // Review & Feedback
  { id: 'category-review', feature: 'Review & Feedback', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'review-list', feature: 'Review List', basic: true, standard: true, premium: true },
  { id: 'customer-review', feature: 'Customer Review (Rate staff, quality, satisfaction)', basic: false, standard: true, premium: true },
  { id: 'high-ranking', feature: 'High-Ranking Upgrade Ratings', basic: false, standard: false, premium: true },

  // Scheduling & Appointments
  { id: 'category-scheduling', feature: 'Scheduling & Appointments', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'book-appointment', feature: 'Book Appointment', basic: false, standard: true, premium: true },
  { id: 'turn-tracker', feature: 'Turn Tracker', basic: false, standard: true, premium: true },
  { id: 'online-appointment', feature: 'Online Appointment Booking', basic: false, standard: false, premium: true },

  // Staff Tracking
  { id: 'category-staff-tracking', feature: 'Staff Tracking', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'today-staff-update', feature: 'Today\'s Staff Update', basic: false, standard: true, premium: true },
  { id: 'staff-workin-status', feature: 'Staff Work-in Status', basic: false, standard: true, premium: true },
  { id: 'update-staff-worktime', feature: 'Update Staff Work Time', basic: false, standard: true, premium: true },

  // Advanced Features
  { id: 'category-advanced', feature: 'Advanced Features', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'roles-permissions', feature: 'Roles & Permissions', basic: false, standard: false, premium: true },
  { id: 'sms-email-settings', feature: 'SMS & Email Settings', basic: false, standard: false, premium: true },
  { id: 'marketing-automation', feature: 'Marketing Automation', basic: false, standard: false, premium: true },
  { id: 'multi-branch', feature: 'Multi-Branch Management', basic: false, standard: false, premium: true },
  { id: 'unlimited-devices', feature: 'Unlimited Devices', basic: false, standard: false, premium: true },

  // Reporting & Analytics
  { id: 'category-reporting', feature: 'Reporting & Analytics', basic: false, standard: false, premium: false, isCategory: true },
  { id: 'basic-reports', feature: 'Basic Reports', basic: true, standard: true, premium: true },
  { id: 'report-by-staff', feature: 'Report by Staff', basic: false, standard: true, premium: true },
  { id: 'customer-reports', feature: 'Customer Reports', basic: false, standard: true, premium: true },
  { id: 'advanced-commissions', feature: 'Advanced Commissions Reporting', basic: false, standard: false, premium: true },
];

export const faqItems: FAQItem[] = [
  {
    id: 1,
    question: 'How does the billing cycle work?',
    answer:
      'Our billing cycle operates on a monthly basis. You will be charged on the same date each month that you initially subscribed. For annual plans, you will be billed once per year on your subscription anniversary date. All charges are processed automatically using the payment method you have on file.',
  },
  {
    id: 2,
    question: 'Can I switch plans at any time?',
    answer:
      'Yes, you can switch between plans at any time. When you upgrade, you will be charged a prorated amount for the remainder of your current billing cycle. When you downgrade, the changes will take effect at the start of your next billing cycle. You can manage your plan changes directly from your account settings.',
  },
  {
    id: 3,
    question: 'Is there a free trial available?',
    answer:
      'Yes! We offer a 30-day free trial for all new users. During this period, you have full access to all features of your chosen plan. No credit card is required to start your trial. You can cancel anytime during the trial period without any charges.',
  },
  {
    id: 4,
    question: 'What is your refund policy?',
    answer:
      'We offer a 30-day money-back guarantee for all paid subscriptions. If you are not satisfied with our service within the first 30 days of your paid subscription, contact our support team for a full refund. Refunds are processed within 5-7 business days to your original payment method.',
  },
  {
    id: 5,
    question: 'Do plans auto-renew?',
    answer:
      'Yes, all plans are set to auto-renew by default to ensure uninterrupted service. You will receive email notifications before each renewal. You can disable auto-renewal at any time from your account settings. If auto-renewal is disabled, your subscription will expire at the end of your current billing period.',
  },
  {
    id: 6,
    question: 'How many staff members and devices can I use?',
    answer:
      'The number of staff members and devices varies by plan. Basic plan supports up to 5 staff members and 3 devices. Standard plan supports up to 15 staff members and 10 devices. Premium plan includes unlimited staff members and unlimited devices. You can add additional staff or devices by upgrading your plan.',
  },
  {
    id: 7,
    question: 'Is my data secure?',
    answer:
      'Absolutely. We take data security seriously and use industry-standard encryption to protect your information. All data is stored on secure servers with regular backups. We comply with GDPR and other data protection regulations. Your customer data, financial information, and business records are encrypted both in transit and at rest.',
  },
  {
    id: 8,
    question: 'Can I import my existing customer data?',
    answer:
      'Yes, we provide data import tools to help you migrate your existing customer data. You can import customer information, appointment history, and service records through our CSV import feature or by contacting our support team for assistance with bulk data migration. We offer free data migration support for all new customers.',
  },
];

export const supportOptions: SupportOption[] = [
  { id: 1, title: 'Email Support', description: 'Get answers within 24 hours', contact: 'support@nailsupply.com', type: 'email', icon: mdiEmail },
  { id: 2, title: 'Phone Hotline', description: 'Mon-Fri, 9AM-6PM EST', contact: '+1 (234) 567-890', type: 'phone', icon: mdiPhone },
  { id: 3, title: 'Live Chat', description: 'Available during business', contact: 'Start Chat', type: 'chat', icon: mdiChat },
];

export const infoCards: InfoCard[] = [
  {
    id: 1,
    title: 'Payment Information',
    icon: mdiCreditCard,
    isOrange: false,
    sections: [
      { title: 'Billing Cycle', description: 'Choose monthly or annual billing. Annual billing saves you 20% compared to monthly plans.' },
      { title: 'Payment Methods', description: 'We accept all major credit card, PayPal, and ACH bank transfers for annual plans.' },
      { title: 'Secure Payments', description: 'Bank-level encryption and PCI DSS compliant payment processing.' },
    ],
  },
  {
    id: 2,
    title: 'Refund Policy',
    icon: mdiShield,
    isOrange: true,
    sections: [
      { title: '30-Day Money-Back Guarantee', description: 'If you\'re not satisfied within the first 30 days, contact our support team for a full refund. Refunds are processed within 5-7 business days.' },
      { title: 'Auto-Renewal', description: 'All plans auto-renew to ensure continuous service. You can disable auto-renewal at any time.' },
      { title: 'Cancellation', description: 'Cancel anytime. Your access continues until the end of your billing period.' },
    ],
  },
];
